import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ArrowRight } from "lucide-react";
import { COMPANY } from "../data/mock";

export const AboutPage = () => (
  <main className="max-w-7xl mx-auto px-4 py-16">
    <h1 className="text-5xl font-bold text-zinc-900 mb-6">About Design Plus</h1>
    <p className="text-lg text-zinc-700 leading-relaxed max-w-3xl">An independent print studio in the heart of Fort Lauderdale. We make business cards, banners, stickers and stationery that feel as serious as the brands we serve. Heavy stocks. Sharp color. Humans who pick up the phone.</p>
    <div className="grid md:grid-cols-3 gap-6 mt-12">
      {[
        { n: "15+", l: "years printing" },
        { n: "10,000+", l: "local brands served" },
        { n: "4.9/5", l: "verified review average" },
      ].map((s) => (
        <div key={s.l} className="bg-rose-50 rounded-xl p-8">
          <div className="text-4xl font-bold text-rose-500">{s.n}</div>
          <div className="text-sm text-zinc-700 mt-1">{s.l}</div>
        </div>
      ))}
    </div>
    <div className="mt-12 prose prose-zinc max-w-3xl">
      <h2 className="text-2xl font-bold mt-10 mb-3">Why we exist</h2>
      <p className="text-zinc-700 leading-relaxed">Most online print shops are quote-and-ship factories. We're different. Every order is reviewed by a real designer before it hits press, and you can walk into our shop on Sunrise Blvd, hold the cardstock and pick your finish in person.</p>
      <h2 className="text-2xl font-bold mt-10 mb-3">Our reprint promise</h2>
      <p className="text-zinc-700 leading-relaxed">If anything we print is wrong on our end — color, alignment, finish — we reprint and ship it. Free. Every time.</p>
    </div>
  </main>
);

export const ContactPage = () => (
  <main className="max-w-7xl mx-auto px-4 py-16">
    <h1 className="text-5xl font-bold text-zinc-900 mb-4">Contact Design Plus</h1>
    <p className="text-lg text-zinc-700 max-w-2xl">Questions, quotes or just want to swing by? We're here Monday through Saturday.</p>
    <div className="grid md:grid-cols-2 gap-10 mt-12">
      <div className="space-y-5">
        <div className="flex gap-4"><MapPin className="w-5 h-5 text-rose-500 mt-1" /><div><div className="font-bold">Visit our studio</div><div className="text-zinc-600">{COMPANY.address}</div></div></div>
        <div className="flex gap-4"><Phone className="w-5 h-5 text-rose-500 mt-1" /><div><div className="font-bold">Call us</div><a href={`tel:${COMPANY.phoneRaw}`} className="text-zinc-600 hover:text-rose-500">{COMPANY.phone}</a></div></div>
        <div className="flex gap-4"><Mail className="w-5 h-5 text-rose-500 mt-1" /><div><div className="font-bold">Email</div><a href={`mailto:${COMPANY.email}`} className="text-zinc-600 hover:text-rose-500">{COMPANY.email}</a></div></div>
        <div className="flex gap-4"><Clock className="w-5 h-5 text-rose-500 mt-1" /><div><div className="font-bold">Hours</div><div className="text-zinc-600">{COMPANY.hours}</div></div></div>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); alert("Thanks! We'll be in touch within one business day."); e.target.reset(); }} className="bg-zinc-50 rounded-xl p-7 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input required placeholder="First name" className="px-4 py-3 rounded-md border border-zinc-200 outline-none focus:border-rose-400 text-sm" />
          <input required placeholder="Last name" className="px-4 py-3 rounded-md border border-zinc-200 outline-none focus:border-rose-400 text-sm" />
        </div>
        <input required type="email" placeholder="Email" className="w-full px-4 py-3 rounded-md border border-zinc-200 outline-none focus:border-rose-400 text-sm" />
        <input placeholder="Phone" className="w-full px-4 py-3 rounded-md border border-zinc-200 outline-none focus:border-rose-400 text-sm" />
        <textarea required placeholder="How can we help?" rows={4} className="w-full px-4 py-3 rounded-md border border-zinc-200 outline-none focus:border-rose-400 text-sm resize-none" />
        <button className="w-full bg-zinc-900 text-white py-3.5 rounded-md text-sm font-bold hover:bg-rose-500 transition-colors">Send message</button>
      </form>
    </div>
  </main>
);

export const QuotePage = () => (
  <main className="max-w-3xl mx-auto px-4 py-16">
    <h1 className="text-5xl font-bold text-zinc-900 mb-3">Request a Quote</h1>
    <p className="text-lg text-zinc-700">Big project? Custom dimensions? Tell us about it — we'll send back a written quote within one business day.</p>
    <form onSubmit={(e) => { e.preventDefault(); alert("Quote request submitted! We'll reply within 1 business day."); e.target.reset(); }} className="mt-10 space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <input required placeholder="Your name" className="px-4 py-3 rounded-md border border-zinc-200 outline-none focus:border-rose-400 text-sm" />
        <input placeholder="Company (optional)" className="px-4 py-3 rounded-md border border-zinc-200 outline-none focus:border-rose-400 text-sm" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input required type="email" placeholder="Email" className="px-4 py-3 rounded-md border border-zinc-200 outline-none focus:border-rose-400 text-sm" />
        <input required placeholder="Phone" className="px-4 py-3 rounded-md border border-zinc-200 outline-none focus:border-rose-400 text-sm" />
      </div>
      <select required className="w-full px-4 py-3 rounded-md border border-zinc-200 outline-none focus:border-rose-400 text-sm bg-white">
        <option value="">What are you printing?</option>
        <option>Business Cards</option>
        <option>Flyers / Postcards</option>
        <option>Banners / Signs</option>
        <option>Labels / Stickers</option>
        <option>Stationery / Forms</option>
        <option>Gang Sheet (DTF)</option>
        <option>Other / Mixed</option>
      </select>
      <textarea required rows={5} placeholder="Describe the project: sizes, quantity, finish, deadline…" className="w-full px-4 py-3 rounded-md border border-zinc-200 outline-none focus:border-rose-400 text-sm resize-none" />
      <button className="bg-zinc-900 text-white py-3.5 px-8 rounded-md text-sm font-bold hover:bg-rose-500 transition-colors inline-flex items-center gap-2">Submit quote request <ArrowRight className="w-4 h-4" /></button>
    </form>
  </main>
);

export const BlogIndexPage = () => {
  const { BLOG_POSTS } = require("../data/mock");
  return (
    <main className="max-w-7xl mx-auto px-4 py-16">
      <h1 className="text-5xl font-bold text-zinc-900 mb-3">From the Design Plus Blog</h1>
      <p className="text-lg text-zinc-700 mb-12 max-w-2xl">Print tips, brand inspiration and local stories from Broward County.</p>
      <div className="grid md:grid-cols-3 gap-6">
        {BLOG_POSTS.map((post) => (
          <Link key={post.slug} to={`/blog/${post.slug}`} className="group block">
            <div className="aspect-[4/3] rounded-lg overflow-hidden bg-zinc-100">
              <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="mt-4 text-xs text-rose-500 uppercase tracking-wider font-bold">{post.category} · {post.date}</div>
            <h3 className="text-xl font-bold text-zinc-900 mt-2 group-hover:text-rose-500 transition-colors">{post.title}</h3>
            <p className="text-sm text-zinc-600 mt-2 leading-relaxed line-clamp-3">{post.excerpt}</p>
          </Link>
        ))}
      </div>
    </main>
  );
};
